<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import Link from 'svelte-navaid/Link.svelte';
	import { navigate } from 'svelte-navaid';
	// Stores
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	import SportcastSelectionStore from '@/Stores/Sport/SportcastSelectionStore.js';
	// Services
	import {
		getIconForSport,
		getUrlParameter,
		paginationBuilder,
		routeForSportEvent,
		setUrlParameter
	} from '@/Services/Helpers.js';
	import { notify } from '@/Services/Notify.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Polling from '@/Components/Polling.svelte';
	import Helmet from '@/Components/Helmet.svelte';
	// Partials
	import ManualResult from '@/Partials/Sport/Markets/ManualResult/_ManualResult.svelte';
	import ManualResultSGM from '@/Partials/Sport/Markets/ManualResult/_ManualResultSGM.svelte';

	$: links =
		$MarketStore && !$MarketStore.id && $MarketStore.id !== null && paginationBuilder(MarketStore.getPagination());

	let openSections = [];
	$: loading = getUnresultedMarkets();
	$: unresultedMarkets = $MarketStore && $MarketStore.sortByDesc('sport_event.start_time');
	$: unresultedSGMSelections =
		$SportcastSelectionStore && $SportcastSelectionStore.sortByDesc('sport_event.start_time');
	$: eventHeadings = unresultedMarkets && unresultedSGMSelections && getSportEventHeadings();

	let pageNo = getUrlParameter('page') || 1;

	let showManualResult = false;
	let showManualResultSGM = false;
	let market;
	let event;
	let selection;

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			loading = getUnresultedMarkets();
			setUrlParameter('page', pageNo);
		}
	}

	function getUnresultedMarkets() {
		SportcastSelectionStore.getUnresultedSelections({ size: 200, number: pageNo }).catch((e) => {
			console.error(e);
			notify({
				type: 'danger',
				title: 'Error!',
				message: (e.error && e.error[0]) || 'problem occurred getting SGM selection, try refreshing the page',
				timeout: 5
			});
		});
		return MarketStore.getUnresultedMarkets({ size: 200, number: pageNo }).catch((e) => {
			console.error(e);
			notify({
				type: 'danger',
				title: 'Error!',
				message: (e.error && e.error[0]) || 'problem occurred getting sport markets, try refreshing the page',
				timeout: 5
			});
		});
	}

	function click(resultMarket) {
		market = resultMarket;
		event = resultMarket.sport_event;
		showManualResult = true;
	}

	function clickSGM(resultSelection) {
		selection = resultSelection;
		event = resultSelection.sport_event;
		showManualResultSGM = true;
	}

	function toggleSportEventCollapse(sportEventId) {
		if (openSections.includes(sportEventId)) {
			openSections = openSections.filter((openSection) => {
				return openSection !== sportEventId;
			});
			return;
		}
		openSections = [...openSections, sportEventId];
	}

	function getSportEventHeadings() {
		const eventHeadings = [];
		unresultedMarkets.each((item) => {
			let exists = false;
			for (const header of eventHeadings) {
				if (header.sport_event.id == item.sport_event.id) {
					exists = true;
				}
			}
			if (!exists) {
				eventHeadings.push({
					sport_event: item.sport_event
				});
			}
		});
		unresultedSGMSelections.each((item) => {
			let exists = false;
			for (const header of eventHeadings) {
				if (header.sport_event.id == item.sport_event.id) {
					exists = true;
				}
			}
			if (!exists) {
				eventHeadings.push({
					sport_event: item.sport_event
				});
			}
		});
		return eventHeadings;
	}
</script>

<Helmet title="Unresulted Market List" />

<Polling pollFunction={getUnresultedMarkets} frequency={60} />
{#if showManualResult}
	<ManualResult
		market_id={market.id}
		eventName={event.name}
		bind:open={showManualResult}
		on:resulted={getUnresultedMarkets}
	/>
{/if}
{#if showManualResultSGM}
	<ManualResultSGM
		selection_id={selection.id}
		eventName={event.name}
		bind:open={showManualResultSGM}
		on:resulted={getUnresultedMarkets}
	/>
{/if}

<div>
	<div>
		<!-- Page Header -->
		<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
			<div class="flex items-center w-full">
				<div class="flex items-center justify-start md:w-1/3">
					<h1 class="text-xl font-bold">Unresulted Markets</h1>
				</div>
			</div>
		</div>

		{#await loading}
			<Loading />
		{:then}
			<!-- /Page Header -->
			<div class="p-2 mx-4 bg-gray-100 rounded">
				<Pagination {links} on:click={changePage} className="mt-2" />
				<div class="overflow-x-auto rounded">
					<table class="w-full whitespace-nowrap">
						<thead>
							<tr class="text-xs text-left text-gray-500 uppercase">
								<th class="px-6 pt-3 pb-4 font-medium">Event/Market</th>
								<th class="px-6 pt-3 pb-4 font-medium" />
							</tr>
						</thead>
						<tbody class="bg-white">
							{#each eventHeadings as eventHeadings (eventHeadings.sport_event.id)}
								<tr
									class="bg-gray-200 cursor-pointer hover:bg-gray-300"
									on:click={() => toggleSportEventCollapse(eventHeadings.sport_event.id)}
								>
									<td class="p-3 px-6 font-semibold border-t">
										<div class="flex flex-shrink">
											<div class="grid grid-rows-2 grid-flow-col">
												<div class="row-span-2 mx-auto my-auto">
													<FontIcon
														icon={eventHeadings.sport_event.sport?.icon ??
															getIconForSport(eventHeadings.sport_event.sport?.name)}
														className="text-3xl"
													/>
												</div>
												<div class="row-span-1 col-span-1">
													<span class="ml-2">
														{eventHeadings.sport_event.name}
														<span class="ml-2 text-sm font-normal">
															{dayjs(eventHeadings.sport_event.start_time).format('D MMM YYYY, HH:mm')}
														</span>
													</span>
												</div>
												<div class="row-span-1 col-span-1 font-medium">
													<span class="ml-2">
														{eventHeadings.sport_event.sport?.name}
														<span class="mx-1">|</span>
														{eventHeadings.sport_event.competition?.name}
													</span>
												</div>
											</div>
										</div>
									</td>
									<td class="w-px px-6 border-t">
										<div class="flex flex-row justify-center items-center">
											<Link href={routeForSportEvent(eventHeadings.sport_event)} class="focus:outline-none">
												<button
													on:click={() => navigate(routeForSportEvent(eventHeadings.sport_event))}
													class="flex flex-grow items-center justify-center btn btn-primary mr-6"
												>
													<span>Go to fixture</span>
												</button>
											</Link>

											{#if openSections.includes(eventHeadings.sport_event.id)}
												<Icon name="chevron-up" classes="block w-4 h-4 text-gray-800 inline" />
											{:else}
												<Icon name="chevron-down" classes="block w-4 h-4 text-gray-800 inline" />
											{/if}
										</div>
									</td>
								</tr>
								{#if openSections.includes(eventHeadings.sport_event.id)}
									{#each unresultedMarkets
										.where('sport_event.id', eventHeadings.sport_event.id)
										.sortBy('name')
										.all() as market (market.id)}
										<tr
											on:click={() => click(market)}
											class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100"
										>
											<td class="py-4 px-6 border-t">{market.name}</td>
											<td class="w-px px-6 border-t text-right">
												<Icon name="chevron-right" classes="block w-4 h-4 text-gray-400 inline" />
											</td>
										</tr>
									{/each}
									{#each unresultedSGMSelections
										.where('sport_event.id', eventHeadings.sport_event.id)
										.sortBy('name')
										.all() as sgmSelection (sgmSelection.id)}
										<tr
											on:click={() => clickSGM(sgmSelection)}
											class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100"
										>
											<td class="py-4 px-6 border-t flex flex-row items-center space-x-2">
												<span>{sgmSelection.name}</span>
												<span class="flex flex-row items-center justify-center">
													<Icon name="sgm" classes="h-auto w-10" />
												</span>
											</td>
											<td class="w-px px-6 border-t text-right">
												<Icon name="chevron-right" classes="block w-4 h-4 text-gray-400 inline" />
											</td>
										</tr>
									{/each}
								{/if}
							{:else}
								<tr>
									<td class="px-6 py-4 text-center border-t" colspan="2"> No Unresulted Markets found.</td>
								</tr>
							{/each}
						</tbody>
					</table>
				</div>
				<Pagination {links} on:click={changePage} className="mt-2" />
			</div>
		{/await}
	</div>
</div>
